import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, afterRenderEffect, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import {
  AppointmentEventPlace,
  PlanningSlotsService,
  SearchArea,
  SlotModel,
  SlotOffer,
  SlotSearchQuery,
  eventPlaceOf,
  isLocated,
  metersBetween,
} from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from '../components/common/callout.component';
import { SearchFieldComponent } from '../components/common/search-field.component';
import { SpinnerComponent } from '../components/common/spinner.component';
import { SEGMENTED_GROUP_CLASS, ToggleDirective } from '../components/common/toggle.directive';
import { LocationMapComponent, LocationMarker, MAP_VIEWS, MapView } from './location-map.component';
import { SearchAreaComponent, createSearchAreaState } from './search-area.component';

/** Which surface publishes the openings. A visit is held by a doctor; an analysis by the branch. */
export type SlotSearchKind = 'appointment' | 'laboratory';

/** An opening taken: who is to deliver it, and when. Together they answer the whole place step. */
export interface SlotChoice {
  offer: SlotOffer;
  slot: SlotModel;
}

/** One day an offer has openings on, so a fortnight of them is read as days rather than as a list. */
interface SlotDay {
  key: string;
  /** The first opening of the day, which is what the day is labelled by. */
  at: string;
  slots: SlotModel[];
}

/** The openings of one day, gathered into the part of it they fall in. */
interface SlotGroup {
  labelKey: string;
  slots: SlotModel[];
}

interface OfferRow {
  offer: SlotOffer;
  /** Why this offer cannot be taken, or empty where it can. */
  refusalKey: string;
  /** How far the branch is from where the search was centred, in kilometres. */
  distanceKm?: number;
  /** How the stamp reads: a window is named by the day it falls on, an hour has to name itself. */
  headerFormat: string;
  /** The soonest it could be delivered — the one thing worth reading off a folded row. */
  nearest: string;
  /** What was taken here, where it was: a folded row then states the answer, not the possibility. */
  chosenAt?: string;
  expanded: boolean;
  holdsChoice: boolean;
  className: string;
}

const ROW_BASE = 'flex w-full flex-col rounded-xl border transition-colors duration-150 motion-reduce:transition-none';
const ROW_CLOSED = `${ROW_BASE} border-neutral-200 bg-neutral-0`;
const ROW_CHOSEN = `${ROW_BASE} border-primary-300 bg-primary-50/40 ring-1 ring-primary-200`;
const ROW_REFUSED = `${ROW_BASE} border-neutral-100 bg-neutral-50`;

const DAY_BASE =
  'flex w-11 shrink-0 snap-start flex-col items-center rounded-lg border py-0.5 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none';
const DAY_OPEN = `${DAY_BASE} border-neutral-200 bg-neutral-0 text-neutral-600 hover:border-primary-300 hover:bg-primary-50/60`;
const DAY_EMPTY = `${DAY_BASE} border-neutral-100 bg-neutral-50 text-neutral-300`;
const DAY_SELECTED = `${DAY_BASE} border-primary-600 bg-primary-600 text-neutral-0`;

const TIME_BASE =
  'inline-flex items-center justify-center gap-1 rounded-md border px-1 py-1 text-sm leading-4 font-semibold tabular-nums transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none';
const TIME_OPEN = `${TIME_BASE} border-neutral-200 bg-neutral-0 text-neutral-700 hover:border-primary-300 hover:bg-primary-50/60`;
const TIME_SELECTED = `${TIME_BASE} border-primary-600 bg-primary-600 text-neutral-0 shadow-xs`;

const WINDOW_BASE =
  'flex w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none';
const WINDOW_OPEN = `${WINDOW_BASE} border-neutral-200 bg-neutral-0 hover:border-primary-300 hover:bg-primary-50/60`;
const WINDOW_SELECTED = `${WINDOW_BASE} border-primary-300 bg-primary-50`;

/**
 * The least a family doctor's calendar covers, matching the member's own app.
 *
 * With one doctor settled on, the question stops being "who is free soonest" and becomes "when is
 * this person next free" — so the days they have nothing are part of the answer and are shown.
 */
const FAMILY_DOCTOR_DAYS = 14;

/** How many offers are laid out at once. A partner network answers with hundreds; a list is shorter. */
const PAGE_SIZE = 25;

/** Where a filter looks for what was typed: everything the row itself puts on the screen. */
const OFFER_FILTERABLE = 6;

/** The place an offer is delivered at, which several offers can share. */
function branchOf(offer: SlotOffer): string {
  return `${offer.partnerId}:${offer.locationId}`;
}

function haystackOf(offer: SlotOffer): string {
  return [offer.doctorName, offer.partnerName, offer.locationName, offer.locationAddress, ...(offer.specializations ?? [])].join(' ').toLowerCase();
}

/**
 * The parts of a day a booking list is read in — the member's own app names and cuts them the same
 * way, «Вранці до 12:00 / В обід з 12:00 до 17:00 / Ввечері після 17:00», so a manager reading a
 * schedule over the phone to a client is reading the same words back.
 */
const DAY_PART_LABEL_KEY = ['guaranteeV2.label.dayPartMorning', 'guaranteeV2.label.dayPartAfternoon', 'guaranteeV2.label.dayPartEvening'];

function dayPartOf(startTime: string): number {
  const hour = new Date(startTime).getHours();

  if (hour < 12) {
    return 0;
  }

  return hour < 17 ? 1 : 2;
}

/** Local rather than UTC: a day at the partner ends when the evening does, not at midnight in Greenwich. */
function dayKeyOf(startTime: string): string {
  const at = new Date(startTime);

  return `${at.getFullYear()}-${at.getMonth() + 1}-${at.getDate()}`;
}

/**
 * Past this length an opening is a window to come within; under it, it books like an hour to
 * arrive at. No laboratory keeps a client this long, and no bookable visit grid is this coarse.
 */
const WINDOW_MINUTES = 90;

function isWindow(slot: SlotModel): boolean {
  return new Date(slot.endTime).getTime() - new Date(slot.startTime).getTime() >= WINDOW_MINUTES * 60 * 1000;
}

function startOfDay(at: Date): Date {
  const start = new Date(at);
  start.setHours(0, 0, 0, 0);

  return start;
}

/**
 * Every day from today to the last one with an opening, gaps included, and never fewer than a
 * fortnight. Stepped a date at a time rather than by adding hours, so the clocks changing does not
 * skip a day or serve one twice.
 */
function continuousDays(found: SlotDay[]): SlotDay[] {
  const cursor = startOfDay(new Date());
  const last = startOfDay(found.length > 0 ? new Date(found[found.length - 1].at) : cursor);

  const days: SlotDay[] = [];

  for (let offset = 0; offset < FAMILY_DOCTOR_DAYS || cursor <= last; offset += 1) {
    const at = cursor.toISOString();
    const key = dayKeyOf(at);

    days.push(found.find(entry => entry.key === key) ?? { key, at, slots: [] });
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

/** What the search is, reduced to the parts a partner would answer differently. */
interface SlotQuestion {
  memberId: string;
  kind: SlotSearchKind;
  ids: string;
  doctorId: string;
  partnerId: string;
  narrowable: boolean;
  area?: SearchArea;
  attempt: number;
}

/** Asked again only where something about the question moved — moving the centre counts. */
function sameQuestion(a: SlotQuestion, b: SlotQuestion): boolean {
  return (
    a.memberId === b.memberId &&
    a.kind === b.kind &&
    a.ids === b.ids &&
    a.doctorId === b.doctorId &&
    a.partnerId === b.partnerId &&
    a.attempt === b.attempt &&
    a.area?.latitude === b.area?.latitude &&
    a.area?.longitude === b.area?.longitude &&
    a.area?.radiusMeters === b.area?.radiusMeters
  );
}

function groupByDay(slots: SlotModel[]): SlotDay[] {
  const days: SlotDay[] = [];

  for (const slot of slots) {
    const day = days.find(entry => entry.key === dayKeyOf(slot.startTime));

    if (day) {
      day.slots.push(slot);
    } else {
      days.push({ key: dayKeyOf(slot.startTime), at: slot.startTime, slots: [slot] });
    }
  }

  return days;
}

/**
 * The openings partners have free for these lines, ready to be taken.
 *
 * Read whole and folded rather than narrowed by a partner first: a manager arranging delivery wants
 * the soonest the service can be had, and which partner that turns out to be is the answer rather
 * than the question. Each offer unfolds into a strip of the days it has free and then the hours of
 * the day chosen, because a fortnight of quarter-hours laid out at once is not a list anybody reads.
 * The hours of a busy day are gathered into morning, afternoon and evening — the way a clinic day is
 * actually spoken about — and left ungathered where there are too few to sort.
 *
 * What an opening is differs between the two kinds: a visit is an hour to arrive at, a laboratory
 * order is a window to come within. So a laboratory's openings are stated as windows across the
 * whole row rather than as a lone chip that looks like an hour nobody chose.
 *
 * The choice is held here and handed up, so a list asked again — the covered lines changed, or the
 * read failed and was retried — can check that what was taken is still on offer and say so when it
 * is not, rather than booking an hour that has since gone.
 */
@Component({
  selector: 'guarantees-slot-picker',
  standalone: true,
  imports: [
    DatePipe,
    MatIcon,
    NgTemplateOutlet,
    TranslatePipe,
    CalloutComponent,
    LocationMapComponent,
    SearchAreaComponent,
    SearchFieldComponent,
    SpinnerComponent,
    ToggleDirective,
  ],
  templateUrl: './slot-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlotPickerComponent implements OnInit {
  memberId = input.required<string>();
  kind = input.required<SlotSearchKind>();
  /** The lines the openings are asked for: what the partner offers follows from the services. */
  prescriptionIds = input.required<string[]>();
  /**
   * Whether a family doctor is being arranged. Only changes what an empty list is allowed to claim:
   * that search reaches integrated partners alone, so nothing found says less than it would for an
   * ordinary visit — and an integration that failed answers the same way.
   */
  familyDoctor = input<boolean>(false);
  /**
   * Narrow the whole search to one doctor at one partner. A family doctor is settled on before any
   * hour is asked about, and the partner's schedule refuses to answer without a doctor named.
   */
  doctorId = input<string | undefined>(undefined);
  partnerId = input<string | undefined>(undefined);
  /** What was taken before, so stepping back and forward again shows the answer rather than blanks. */
  initial = input<SlotChoice | undefined>(undefined);
  /** Where the search was centred before. The step is destroyed on leaving it; the answer is not. */
  initialArea = input<SearchArea | undefined>(undefined);

  changed = output<SlotChoice | undefined>();
  areaChanged = output<SearchArea>();

  protected readonly dayOpen = DAY_OPEN;
  protected readonly dayEmpty = DAY_EMPTY;
  protected readonly daySelected = DAY_SELECTED;
  protected readonly timeOpen = TIME_OPEN;
  protected readonly timeSelected = TIME_SELECTED;
  protected readonly windowOpen = WINDOW_OPEN;
  protected readonly windowSelected = WINDOW_SELECTED;

  protected readonly segmentedGroup = SEGMENTED_GROUP_CLASS;
  protected readonly views = MAP_VIEWS;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly slots = inject(PlanningSlotsService);

  /** Bumped to ask again after a read that failed — nothing about the question has changed. */
  private readonly attempt = signal(0);

  protected readonly loading = signal(true);
  protected readonly failed = signal(false);

  /** Set when a list asked again no longer holds the opening that had been taken. */
  protected readonly choiceLapsed = signal(false);

  private readonly offers = signal<SlotOffer[]>([]);
  private readonly choice = signal<SlotChoice | undefined>(undefined);

  protected readonly term = signal('');
  protected readonly seenOnly = signal(false);

  /**
   * The map first: it is the control that names the place, so the answers arrive on the thing the
   * manager was already looking at. The list is a step away, and is where an hour is actually taken.
   */
  protected readonly view = signal<MapView>('map');

  /** Where the search is centred and how far out; nothing is asked of any partner before it is set. */
  protected readonly searchArea = createSearchAreaState(area => this.areaChanged.emit(area));

  /** The branch whose card is open over the map — a pin taken to be read, not yet an hour taken. */
  private readonly focusedBranch = signal<string | undefined>(undefined);

  /**
   * The offer the card reads. Several doctors can share one pin, so the one already unfolded is
   * preferred where it stands at the focused branch — the card must name the same offer whose
   * days and hours it goes on to show.
   */
  protected readonly focusedOffer = computed<SlotOffer | undefined>(() => {
    const branch = this.focusedBranch();

    if (!branch) {
      return undefined;
    }

    const expanded = this.expanded();

    return expanded && branchOf(expanded) === branch ? expanded : this.matched().find(offer => branchOf(offer) === branch);
  });

  /** How much of the narrowed list is laid out. Reset whenever the narrowing itself changes. */
  private readonly page = signal(1);

  private readonly expandedKey = signal<string | undefined>(undefined);

  protected readonly day = signal<string | undefined>(undefined);

  /**
   * Whether openings here are placed as laboratory orders — a fact about the kind, deciding that
   * no doctor need be named. How an opening is shown follows the opening's own shape instead.
   */
  private readonly spansTheDay = computed(() => this.kind() === 'laboratory');

  /**
   * Whether the day on show is answered in windows rather than hours.
   *
   * What the laboratories publish is split: some state one span for the whole day, others a grid
   * of quarter-hours no different on screen from a clinic's. Each is shown the way it is best
   * picked — a wall of fifteen-minute rows each claiming to be the day's window reads as neither
   * a window nor a schedule.
   */
  protected readonly windowed = computed(() => this.spansTheDay() && this.times().some(isWindow));

  /**
   * Whether a centre and a distance are worth sending at all.
   *
   * A family doctor search ignores them on the other side — the person is chosen before the place —
   * and a search already pinned to one partner or one doctor is as narrow as it can usefully be, so
   * a radius on top of it could only hide the branch that was asked for.
   */
  protected readonly narrowable = computed(() => !this.familyDoctor() && !this.doctorId() && !this.partnerId());

  /**
   * Nothing has been asked of anyone yet, because nobody has said where to ask about.
   *
   * Held rather than guessed: a list of one city's clinics offered to a manager arranging care in
   * another is a worse answer than no list, and it arrives looking exactly as authoritative.
   */
  protected readonly awaitingArea = computed(() => this.narrowable() && !this.searchArea.area());

  /**
   * Whether the map can stand in for the list.
   *
   * It cannot where the offers have no points behind them — a partner network our side never mapped
   * answers with addresses and nothing else, and a map of none of them would strand the list behind
   * a view that shows nothing. Empty, though, it maps fine: the circle alone says where we looked.
   */
  private readonly mappable = computed(() => this.matched().length === 0 || this.markers().length > 0);

  /** The map is up while the place is being named, and after that whenever it is the view chosen. */
  protected readonly showMap = computed(() => this.awaitingArea() || (this.view() === 'map' && this.mappable()));

  /**
   * Once wanted, kept: the map is hidden rather than torn down when the list takes the floor, so
   * flicking between the two costs nothing. Never wanted — a search pinned to one doctor, read as
   * a list throughout — it is never built at all, because building one is itself a paid call.
   */
  protected readonly mapLive = signal(false);

  /**
   * Whether any offer could stand on a map at all, read off the whole answer rather than the
   * narrowed one — so the view switch does not vanish under a filter that momentarily matches none.
   */
  protected readonly locatable = computed(() => this.offers().some(offer => isLocated(offer)));

  /** Worth offering a filter at all: three doctors are read, a hundred and sixty-eight are searched. */
  protected readonly filterable = computed(() => this.offers().length > OFFER_FILTERABLE);

  /** Offered only where it narrows something — a network nobody has been to has none to mark. */
  protected readonly offersSeen = computed(() => this.offers().some(offer => offer.seenBefore));

  /**
   * What the typing and the marks leave, over everything the row shows: a manager searching for
   * «Діла Нижній Вал» is naming a partner and a branch in one breath, and a search that asked the
   * words to be in one field would find nothing.
   */
  private readonly matched = computed<SlotOffer[]>(() => {
    const words = this.term().trim().toLowerCase().split(/\s+/).filter(Boolean);
    const seenOnly = this.seenOnly();
    const chosenKey = this.choice()?.offer.key;

    return this.offers().filter(offer => {
      // What is already taken is never filtered away. A narrowing that hid it would leave the step
      // looking unanswered while an hour stood booked behind it, and the row states plainly enough
      // that it is the one chosen.
      if (offer.key === chosenKey) {
        return true;
      }

      if (seenOnly && !offer.seenBefore) {
        return false;
      }

      const haystack = haystackOf(offer);

      return words.every(word => haystack.includes(word));
    });
  });

  protected readonly matchCount = computed(() => this.matched().length);

  /** What is left beyond the page, so the button can say how much rather than merely that there is. */
  protected readonly hiddenCount = computed(() => Math.max(0, this.matched().length - this.page() * PAGE_SIZE));

  /** The list found openings and the narrowing left none — a different thing from nobody being free. */
  protected readonly noMatches = computed(() => !this.loading() && !this.failed() && this.offers().length > 0 && this.matched().length === 0);

  protected readonly rows = computed<OfferRow[]>(() => {
    const expandedKey = this.expandedKey();
    const choice = this.choice();

    return this.matched()
      .slice(0, this.page() * PAGE_SIZE)
      .map(offer => {
        const holdsChoice = offer.key === choice?.offer.key;
        const refusalKey = this.refusalKeyOf(offer);
        const stamped = holdsChoice && choice ? choice.slot : offer.slots[0];

        return {
          offer,
          refusalKey,
          distanceKm: this.distanceKmOf(offer),
          headerFormat: this.spansTheDay() && isWindow(stamped) ? 'd MMM' : 'd MMM HH:mm',
          nearest: offer.slots[0].startTime,
          chosenAt: holdsChoice ? choice?.slot.startTime : undefined,
          expanded: offer.key === expandedKey,
          holdsChoice,
          className: refusalKey ? ROW_REFUSED : holdsChoice ? ROW_CHOSEN : ROW_CLOSED,
        };
      });
  });

  /**
   * A visit is placed against a named person, so a branch that publishes hours under nobody has
   * none to place it against. Said rather than hidden: a partner missing from the list reads as a
   * partner with nothing free, which is a different answer and sends the manager looking elsewhere.
   */
  private refusalKeyOf(offer: SlotOffer): string {
    return this.spansTheDay() || offer.doctorId ? '' : 'guaranteeV2.message.slotNeedsDoctor';
  }

  /**
   * Nobody is free — which is a fact about the partners, not about what has been typed. Read off the
   * whole answer rather than off the narrowed one: a term matching nothing would otherwise replace
   * the box it was typed into, and there would be no way back to the list.
   */
  protected readonly empty = computed(() => !this.loading() && !this.failed() && this.offers().length === 0);

  private readonly expanded = computed(() => this.offers().find(offer => offer.key === this.expandedKey()));

  protected readonly days = computed<SlotDay[]>(() => {
    const found = groupByDay(this.expanded()?.slots ?? []);

    return this.familyDoctor() ? continuousDays(found) : found;
  });

  private readonly selected = computed<SlotDay | undefined>(() => this.days().find(day => day.key === this.day()));

  protected readonly times = computed<SlotModel[]>(() => this.selected()?.slots ?? []);

  /**
   * The month the strip is standing in, named once above it. A fortnight that repeats the month in
   * every cell spends fourteen labels saying one thing, and each of them costs a line of height.
   */
  protected readonly month = computed(() => this.selected()?.at);

  /** The day's openings by the part of the day they fall in, skipping the parts that hold none. */
  protected readonly groups = computed<SlotGroup[]>(() => {
    const parts: SlotModel[][] = [[], [], []];

    for (const slot of this.times()) {
      parts[dayPartOf(slot.startTime)].push(slot);
    }

    return parts.map((slots, index) => ({ labelKey: DAY_PART_LABEL_KEY[index], slots })).filter(group => group.slots.length > 0);
  });

  /** Named only where the naming sorts something: one heading over the whole day sorts nothing. */
  protected readonly grouped = computed(() => this.groups().length > 1);

  constructor() {
    effect(() => {
      if (this.showMap()) {
        this.mapLive.set(true);
      }
    });

    combineLatest([
      toObservable(this.memberId),
      toObservable(this.kind),
      toObservable(this.prescriptionIds),
      toObservable(this.doctorId),
      toObservable(this.partnerId),
      toObservable(this.narrowable),
      toObservable(this.searchArea.area),
      toObservable(this.attempt),
    ])
      .pipe(
        // The lines are the question, and a step returned to hands back the same set in a new array.
        map(([memberId, kind, prescriptionIds, doctorId, partnerId, narrowable, area, attempt]) => ({
          memberId,
          kind,
          ids: [...prescriptionIds].sort().join(','),
          doctorId: doctorId ?? '',
          partnerId: partnerId ?? '',
          narrowable,
          area: narrowable ? area : undefined,
          attempt,
        })),
        // Nothing is asked of a partner before the area is settled: that search would sweep the
        // country, and the narrowed one would replace its answer a moment later anyway.
        filter(question => !question.narrowable || !!question.area),
        distinctUntilChanged(sameQuestion),
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
          this.choiceLapsed.set(false);
          this.offers.set([]);
          this.page.set(1);
          this.focusedBranch.set(undefined);
        }),
        switchMap(question => {
          const area = question.area;
          const query: SlotSearchQuery = {
            prescriptionIds: question.ids.split(','),
            doctorId: question.doctorId || undefined,
            partnerId: question.partnerId || undefined,
            // A centre without a distance narrows nothing on the other side, so they go together.
            latitude: area?.latitude,
            longitude: area?.longitude,
            maxDistanceMeters: area?.radiusMeters,
          };

          const search = question.kind === 'laboratory' ? this.slots.laboratorySlots(question.memberId, query) : this.slots.appointmentSlots(question.memberId, query);

          return search.pipe(
            catchError(() => {
              this.failed.set(true);

              return of<SlotOffer[]>([]);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(offers => {
        this.offers.set(offers);
        this.loading.set(false);

        // A read that failed is not the partner saying the hour has gone, so what was taken stands.
        if (!this.failed()) {
          this.settle(offers);
        }
      });

    // After render, not during it: the strip is built by the same change that picks the day, and a
    // day chosen out of sight — stepping back into an answer given on the tenth of a fortnight —
    // would otherwise sit off the end of a strip that looks like nothing is chosen at all.
    afterRenderEffect(() => {
      const day = this.day();

      if (day) {
        this.host.nativeElement.querySelector(`[data-day="${day}"]`)?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    });

    // The unfolded row is brought to the reader, wherever in the list it stands — a row opened
    // from a pin, or restored with the step, may otherwise sit pages of scroll below the fold.
    afterRenderEffect(() => {
      const key = this.expandedKey();

      if (key && !this.showMap()) {
        this.host.nativeElement.querySelector(`[data-offer="${CSS.escape(key)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  /** Read here rather than in the constructor: an input has no value until Angular has bound it. */
  ngOnInit(): void {
    // The map is how a place is named, so it opens there — unless there is nothing to name, or an
    // hour was already taken, in which case the list is what the manager came back for.
    this.view.set(!this.narrowable() || this.initial() ? 'list' : 'map');

    // Only where the map opens with nowhere to stand: an area already named frames itself.
    const area = this.initialArea();
    if (area) {
      this.searchArea.restore(area);
    } else if (this.narrowable()) {
      this.searchArea.previewHome();
    }

    const initial = this.initial();
    if (initial) {
      this.choice.set(initial);
      this.expandedKey.set(initial.offer.key);
      this.day.set(dayKeyOf(initial.slot.startTime));
    }
  }

  /** Unfolds the row — and moves the map card with it, so the two surfaces never read different rows. */
  protected toggle(row: OfferRow): void {
    if (row.refusalKey) {
      return;
    }

    if (row.expanded) {
      this.expandedKey.set(undefined);
      this.focusedBranch.set(undefined);

      return;
    }

    this.expandedKey.set(row.offer.key);
    this.day.set(groupByDay(row.offer.slots)[0]?.key);
    this.focusedBranch.set(branchOf(row.offer));
  }

  /**
   * A day whose only opening is the whole of it is answered by picking the day.
   *
   * A laboratory that takes samples between two hours offers one window a day, so a second click on
   * the one thing offered asks the manager to confirm what they have already said. Where a day holds
   * hours to choose between, picking the day only narrows to them.
   */
  protected chooseDay(entry: SlotDay): void {
    this.day.set(entry.key);

    if (this.spansTheDay() && entry.slots.length === 1 && isWindow(entry.slots[0])) {
      this.choose(entry.slots[0]);
    }
  }

  protected choose(slot: SlotModel): void {
    const offer = this.expanded();
    if (!offer) {
      return;
    }

    this.choiceLapsed.set(false);
    this.choice.set({ offer, slot });
    this.focusedBranch.set(branchOf(offer));
    this.changed.emit({ offer, slot });
  }

  protected isChosen(slot: SlotModel): boolean {
    const choice = this.choice();

    return choice?.offer.key === this.expandedKey() && choice?.slot.startTime === slot.startTime;
  }

  /** Marked where it is not an ordinary visit at the branch, since that is what a time otherwise means. */
  protected remote(slot: SlotModel): boolean {
    return eventPlaceOf(slot) === AppointmentEventPlace.Telemedicine;
  }

  /**
   * How far the branch is from where the search was centred.
   *
   * Only the laboratory answer states a distance of its own, so it is worked out here for both —
   * a radius the manager set means little without the figures it let through.
   */
  protected distanceKmOf(offer: SlotOffer): number | undefined {
    const area = this.searchArea.area();

    if (!area || !isLocated(offer)) {
      return undefined;
    }

    return Math.round(metersBetween(area, { latitude: offer.latitude!, longitude: offer.longitude! }) / 100) / 10;
  }

  /**
   * The branches behind the offers, one pin each.
   *
   * Folded by branch rather than by offer: three doctors free at the same clinic are three rows and
   * one place, and three pins on one rooftop is a worse answer than one.
   */
  protected readonly markers = computed<LocationMarker[]>(() => {
    const seen = new Set<string>();

    return this.matched().reduce<LocationMarker[]>((points, offer) => {
      const id = branchOf(offer);

      if (!seen.has(id) && isLocated(offer)) {
        seen.add(id);
        points.push({ id, title: `${offer.partnerName} — ${offer.locationName}`, latitude: offer.latitude!, longitude: offer.longitude!, logoUrl: offer.logoUrl });
      }

      return points;
    }, []);
  });

  /** Which pin stands out: the one being read, or failing that the one the hour was taken at. */
  protected readonly markedBranch = computed(() => {
    const choice = this.choice();

    return this.focusedBranch() ?? (choice ? branchOf(choice.offer) : undefined);
  });

  /**
   * A pin taken is read where it was taken: the pin stands out and a card over the map says who is
   * there and when they could — being thrown to a list mid-look is losing one's place on purpose.
   *
   * The list is still prepared behind it — the row unfolded, the page grown to reach it — so the
   * card's one step onward lands on the hours already open rather than on a list to hunt through.
   *
   * The offer already unfolded at the branch is kept: several doctors share a pin, and clicking
   * the pin of the place already being read must not swap the doctor or reset the day.
   */
  protected chooseMarker(id: string): void {
    const offers = this.matched();
    const kept = this.expanded();
    const offer = kept && branchOf(kept) === id && offers.includes(kept) ? kept : offers.find(entry => branchOf(entry) === id);

    if (!offer) {
      return;
    }

    this.focusedBranch.set(id);
    this.page.update(page => Math.max(page, Math.floor(offers.indexOf(offer) / PAGE_SIZE) + 1));

    if (offer !== kept) {
      this.expandedKey.set(offer.key);
      this.day.set(groupByDay(offer.slots)[0]?.key);
    }
  }

  protected dismissCard(): void {
    this.focusedBranch.set(undefined);
  }

  /** Every narrowing starts the list again: a page reached under one term means nothing under another. */
  protected type(value: string): void {
    this.term.set(value);
    this.page.set(1);
  }

  protected toggleSeen(): void {
    this.seenOnly.update(only => !only);
    this.page.set(1);
  }

  protected clearFilter(): void {
    this.term.set('');
    this.seenOnly.set(false);
    this.page.set(1);
  }

  protected showMore(): void {
    this.page.update(page => page + 1);
  }

  protected retry(): void {
    this.attempt.update(count => count + 1);
  }

  /**
   * An opening stays taken only while it is still on offer. A list asked again is the partner's
   * answer as it stands now, and an hour that has gone from it has gone — better said here than
   * refused by the partner after the manager has left the dialog.
   */
  private settle(offers: SlotOffer[]): void {
    const choice = this.choice();
    if (!choice) {
      this.expandedKey.set(offers[0]?.key);
      this.day.set(groupByDay(offers[0]?.slots ?? [])[0]?.key);

      return;
    }

    const offer = offers.find(entry => entry.key === choice.offer.key);
    const slot = offer?.slots.find(entry => entry.startTime === choice.slot.startTime);

    if (offer && slot) {
      this.choice.set({ offer, slot });
      this.expandedKey.set(offer.key);
      this.day.set(dayKeyOf(slot.startTime));
      // Handed back as the partner now states it: the form drops the hour whenever the lines change,
      // and takes it again only from here — once the same hour is offered for the new set too.
      this.changed.emit({ offer, slot });

      return;
    }

    this.choiceLapsed.set(true);
    this.choice.set(undefined);
    this.expandedKey.set(offers[0]?.key);
    this.day.set(groupByDay(offers[0]?.slots ?? [])[0]?.key);
    this.changed.emit(undefined);
  }
}
